import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const BATTING_CATS = [
  { key: 'homeRuns', label: 'HR' },
  { key: 'battingAverage', label: 'AVG' },
  { key: 'runsBattedIn', label: 'RBI' },
  { key: 'stolenBases', label: 'SB' },
];

const PITCHING_CATS = [
  { key: 'era', label: 'ERA' },
  { key: 'strikeouts', label: 'K' },
  { key: 'wins', label: 'W' },
];

const LeaderRow = ({ rank, name, value, onClick }) => (
  <motion.div
    className="flex items-center gap-2 py-1.5 border-b border-[rgba(101,141,113,0.2)] last:border-0 cursor-pointer group"
    whileHover={{ backgroundColor: 'rgba(216,206,179,0.05)' }}
    onClick={onClick}
  >
    <span className={`text-[10px] font-black tabular-nums w-5 text-center shrink-0 ${rank === 1 ? 'text-[#d8ceb3]' : 'text-[#658d71]'}`}>
      {rank === 1 ? '★' : rank}
    </span>
    <span className="text-[11px] font-bold text-white uppercase tracking-wide truncate flex-1 group-hover:text-[#d8ceb3] transition-colors">
      {name?.split(' ').pop() ?? '--'}
    </span>
    <span className="text-[11px] font-black tabular-nums text-[#a9c9b1] shrink-0">{value ?? '--'}</span>
  </motion.div>
);

const SeasonLeaders = ({ leadersBatting, leadersPitching, onPlayerClick }) => {
  const [group, setGroup] = useState('bat');
  const [catIdx, setCatIdx] = useState(0);

  const cats = group === 'bat' ? BATTING_CATS : PITCHING_CATS;
  const cat = cats[catIdx] || cats[0];

  const source = group === 'bat' ? leadersBatting : leadersPitching;
  const categoryData = source?.leagueLeaders?.find(l => l.leaderCategory === cat.key);
  const leaders = categoryData?.leaders ?? [];

  return (
    <div className="border border-[#658d71] bg-[rgba(0,0,0,0.08)] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#658d71] bg-[rgba(0,0,0,0.1)]">
        <span className="text-xs font-black uppercase tracking-widest text-[#d8ceb3]">Team Leaders</span>
        <div className="flex gap-1">
          {['bat', 'pit'].map(v => (
            <button
              key={v}
              onClick={() => { setGroup(v); setCatIdx(0); }}
              className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 border transition-colors ${
                group === v
                  ? 'bg-[#658d71] border-[#a9c9b1] text-white'
                  : 'border-[#658d71] text-[#a9c9b1] hover:text-white'
              }`}
            >
              {v === 'bat' ? 'Bat' : 'Pit'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 px-3 pt-2 flex-wrap">
        {cats.map((c, i) => (
          <button
            key={c.key}
            onClick={() => setCatIdx(i)}
            className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 border transition-colors ${
              catIdx === i
                ? 'bg-[rgba(216,206,179,0.15)] border-[#d8ceb3] text-[#d8ceb3]'
                : 'border-[#658d71] text-[#658d71] hover:text-[#a9c9b1]'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="p-3">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${group}-${cat.key}`}
            initial={{ opacity: 0, x: 6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -6 }}
            transition={{ duration: 0.12 }}
          >
            {leaders.length > 0 ? leaders.map((l, i) => (
              <LeaderRow
                key={i}
                rank={l.rank ?? i + 1}
                name={l.person?.fullName}
                value={l.value}
                onClick={() => l.person?.id && onPlayerClick && onPlayerClick(l.person.id, l.person.fullName)}
              />
            )) : (
              <p className="text-[#658d71] text-xs text-center py-6 uppercase tracking-widest">No data available</p>
            )}
          </motion.div>
        </AnimatePresence>
        <p className="text-[8px] text-[#658d71] tracking-widest uppercase mt-2">Click player to view profile</p>
      </div>
    </div>
  );
};

export default SeasonLeaders;